import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { useEventHandler } from 'hook/event.hook';
import moment from 'moment';
import { fetchPersonalLoanPreApprovals } from 'externals/_services/product.service';
import { hashCode } from 'helpers/string.helper';
import { PL_PREQUAL_RESULT_KEY } from 'actions/datacache.actions';
import { cachePayloadForKey, selectForKey } from 'actions/datacache.actions';
import { useLogger } from 'hook/error.hook';
import { wrapService } from 'actions/service_wrapper.actions';
import { useComplexState } from 'hook/helper.hook';
import { PERSONAL_LOAN_VERTICAL } from 'types/marketplaceTiles.constants';
import { setStaticModuleOfMarketplaceOffer } from 'actions/offers.actions';
import { RESET_SELECT_VERTICAL_TO_MARKETPLACE } from 'types/offerMarketplace.constants';
import { PL_PREQUAL_OFFER_MODULE } from 'types/morph.pagePosition.constants';
const a_fetchPersonalLoanPreApprovals = wrapService(
  fetchPersonalLoanPreApprovals,
  {
    name: 'fetchPersonalLoanPreApprovals',
  },
);

const sortByMap = displayName => {
  switch (displayName) {
    case 'Lowest APR':
      return 'APR';
    case 'Shortest Term':
      return 'TERM';
    case 'Lowest Monthly Payment':
      return 'MONTHLY_PAYMENTS';
    case 'Highest Loan Amount':
      return '-LOAN_AMOUNT';
    case 'Lowest Loan Amount':
    default:
      return 'LOAN_AMOUNT';
  }
};

const sortByList = [
  'Lowest APR',
  'Shortest Term',
  'Lowest Monthly Payment',
  'Lowest Loan Amount',
  'Highest Loan Amount',
];

function sortSameAmountOffers(offerList, isMonthlyPayment) {
  offerList.sort((offerA, offerB) => {
    let result = isMonthlyPayment
      ? offerA.monthlyPayment < offerB.monthlyPayment
      : offerA.termMonths < offerB.termMonths;
    if (result === false) {
      result = -1;
    }
    return result * -1;
  });
}

const getFirstSameAmountOffer = (offerList, badgeLabel) => {
  if (offerList && offerList[0]) {
    const firstOffer = Object.assign({}, offerList[0]);
    firstOffer.badgeLabel = badgeLabel;
    return firstOffer;
  } else {
    return null;
  }
};

const getOtherAmountOffers = (prequalResult, formInput) => {
  let offers = null;
  if (prequalResult && prequalResult.offers) {
    offers = prequalResult.offers.filter(
      offers => offers.loanAmount !== +formInput.amount,
    );
  }
  return offers;
};

export const filterSameAmountOffers = (offers, loanAmount) => {
  let offerList = offers?.filter(offers => offers.loanAmount === +loanAmount);
  const finalList = [];
  if (offerList?.length > 0) {
    const lowestAPR = getFirstSameAmountOffer(offerList, 'LOWEST APR');

    sortSameAmountOffers(offerList, true);
    const lowestMonthlyPayment = getFirstSameAmountOffer(
      offerList,
      'LOWEST MONTHLY PAYMENT',
    );

    offerList = offerList.filter(offers => !offers.displayTerm);

    sortSameAmountOffers(offerList, false);
    const shortestTerm = getFirstSameAmountOffer(offerList, 'SHORTEST TERM');

    const remainingOffers = offerList.filter(
      offer =>
        offer.personalLoanId !== lowestAPR.personalLoanId &&
        offer.personalLoanId !== lowestMonthlyPayment.personalLoanId &&
        offer.personalLoanId !== shortestTerm.personalLoanId,
    );

    if (lowestMonthlyPayment) {
      finalList.push(lowestMonthlyPayment);
    }

    if (lowestAPR) {
      finalList.push(lowestAPR);
    }

    if (shortestTerm) {
      finalList.push(shortestTerm);
    }
    finalList.push(...remainingOffers);
  }

  return finalList;
};

export const parsePlPrequalOffer = (offer, index, prequalData) => {
  if (offer) {
    const partnerData = prequalData?.partnerDetails?.find(
      partner => partner.partnerName === offer.partnerName,
    );
    const name = offer.preApprovalVendor
      ? offer.preApprovalVendor
      : offer.partnerName;
    const apr = offer.apr ? offer.apr.toFixed(2) : 0;
    const hashId = hashCode(
      `${offer.productPreApprovalId}-${offer.term}-${offer.loanAmount}-${offer.monthlyPayment}`,
    );

    return {
      linkUrl: offer.preApprovalLink,
      name: name,
      issuer: offer.partnerName,
      partnerCode: offer.partnerCode,
      apr: apr,
      termMonths: offer.term,
      displayTerm: offer.displayTerm,
      partnerTag: offer.partnerTag,
      poweredBy: offer.poweredBy,
      loanAmount: offer.loanAmount,
      monthlyPayment: offer.monthlyPayment,
      imageLink: offer.preApprovalLenderLogoLink,
      bullets: offer.bullets,
      disclaimer: offer.preApprovalLenderDisclaimer,
      offerType: 'PERSONAL_LOAN',
      issuerEnum: '',
      personalLoanId: String(hashId),
      isPrequalOffer: true,
      dueDate: partnerData
        ? moment().add(partnerData.daysToRetryAfter, 'd').format('MM/DD/YYYY')
        : null,
      totalCostDisplay: offer.totalCostDisplay,
      totalCost: offer.totalCost,
      extraProps: {},
      secure: offer?.secure,
      badgeLabel: offer.badge,
      productKey: offer.productKey,
    };
  }
  return null;
};

export const usePersonalLoanPrequalResultState = (
  userInfo,
  prequalFormLoading,
) => {
  const dispatch = useDispatch();
  const eventHandler = useEventHandler();
  const logger = useLogger({
    name: 'PersonalLoanPrequalForm.hook',
  });

  let [prequalResultData] = useSelector(state =>
    selectForKey(PL_PREQUAL_RESULT_KEY, state),
  );

  const [state, setState] = useComplexState({
    loading: true,
    sortLoading: false,
    sortBy: sortByList[0],
    prequalResult: null,
    sameAmountOffers: [],
    showAllSameAmountOffers: false,
    showAllOffers: false,
    fromPrequalForm: prequalResultData
      ? prequalResultData.fromPrequalForm
      : false,
  });
  const setShowAllOffers = v => setState({ showAllOffers: v });
  const [sortBy, setSortBy] = useState(sortByList[0]);

  const {
    loading,
    sortLoading,
    prequalResult,
    sameAmountOffers,
    showAllSameAmountOffers,
    showAllOffers,
    fromPrequalForm,
  } = state;

  const userId = userInfo ? userInfo.userId : '';
  const formInput = prequalResultData ? prequalResultData.formInput : null;

  let otherAmountOffers = getOtherAmountOffers(prequalResult, formInput);
  const hasOffers =
    prequalResult && prequalResult.offers && prequalResult.offers.length > 0;
  const formLoanAmount = formInput ? formInput.amount : 0;

  if (!prequalResultData) {
    prequalResultData = {};
  }

  //init
  useEffect(() => {
    if (userInfo && loading && prequalFormLoading === false) {
      // POD3-1504
      if (prequalResultData.result) {
        setState({
          sameAmountOffers: filterSameAmountOffers(
            prequalResultData.result.offers,
            formInput?.amount,
          ),
          prequalResult: prequalResultData.result,
          loading: false,
        });
      } else if (prequalResultData.formInput) {
        fetchPrequalOffers(null, true);
      } else {
        setState({
          loading: false,
        });
      }
    }
    //eslint-disable-next-line
  }, [prequalFormLoading, userInfo]); // POD3-1504

  function fetchPrequalOffers(sort, firstLoad) {
    setState({
      sortLoading: true,
    });

    /* build api params */
    const extraParams = {
      userId,
      sort,
    };
    if (firstLoad) {
      extraParams.sort = 'UNIFIED_LOAN_AMOUNT_APR';
    }
    if (!firstLoad || !prequalResultData.fromPrequalForm) {
      extraParams.cachedOffers = true;
    }
    const finalParams = extraParams.cachedOffers
      ? extraParams
      : Object.assign(extraParams, formInput);
    /**/
    dispatch(a_fetchPersonalLoanPreApprovals(finalParams))
      .then(result => {
        if (result.offers && result.offers.length > 0) {
          result.offers = result.offers.map((offer, index) =>
            parsePlPrequalOffer(offer, index + 1, result),
          );

          if (!sort || sort === sortByMap(sortByList[0])) {
            prequalResultData.result = result;
            dispatch(
              cachePayloadForKey(PL_PREQUAL_RESULT_KEY, prequalResultData),
            );
          }
          setState({
            sameAmountOffers: firstLoad
              ? filterSameAmountOffers(result.offers, formInput?.amount)
              : sameAmountOffers,
            prequalResult: result,
            loading: false,
            sortLoading: false,
          });
        } else {
          setState({
            prequalResult: 'nodata',
            loading: false,
            sortLoading: false,
          });
        }
      })
      .catch(err => {
        if (err.code !== 'PLPA_1010') {
          logger.showError(
            'A problem happened while fetching offers',
            err.message,
          );
        }
        logger.reportAPIError(err);
        logger.errorException('Exception during prequal offer search', err);
        setState({
          prequalResult: 'error',
          loading: false,
          sortLoading: false,
        });
      });
  }

  function onClickSort(e) {
    if (e) {
      eventHandler(CLICK, {
        'Click Type': 'Prequal Offer Sort',
        Vertical: PERSONAL_LOAN_VERTICAL,
        'Module Name': 'PL Prequal Offer',
      });
    }
  }

  function onSortChange(newValue) {
    eventHandler(CLICK, {
      'Click Type': `Prequal Offer Sort`,
      'Module Name': 'PL Prequal Offer',
      'Module Position': 1,
      Vertical: PERSONAL_LOAN_VERTICAL,
    });
    setSortBy(newValue);
    fetchPrequalOffers(sortByMap(newValue));
  }

  function onClickSearchNoPrequal() {
    eventHandler(CLICK, {
      'Click Type': 'Search without pre-qualification',
      Vertical: PERSONAL_LOAN_VERTICAL,
      'Module Name': 'PL Prequal Offer',
    });
    prequalResultData.fromPrequalResult = true;
    dispatch(cachePayloadForKey(PL_PREQUAL_RESULT_KEY, prequalResultData));
    dispatch(setStaticModuleOfMarketplaceOffer('PERSONAL_LOAN_OFFERS'));
    window.scrollTo(0, 0);
  }

  function onClickShowAllOffers() {
    eventHandler(CLICK, {
      'Click Type': 'See more pre-qualified offers',
      'Page Position': 'Personal Loan Other Loan Amount Prequal Result',
      Vertical: PERSONAL_LOAN_VERTICAL,
      'Module Name': 'PL Prequal Offer',
    });
    setState({
      showAllOffers: true,
    });
  }

  function onClickShowAllSameAmountOffers() {
    eventHandler(CLICK, {
      'Click Type': 'See more pre-qualified offers',
      'Page Position': 'Personal Loan Exact Loan Amount Prequal Result',
      Vertical: PERSONAL_LOAN_VERTICAL,
      'Module Name': 'PL Prequal Offer',
    });
    setState({
      showAllSameAmountOffers: true,
    });
  }

  const showAllPrequalOffers = () => {
    eventHandler(CLICK, {
      'Click Type': 'See All pre-qualified Offers',
      Vertical: PERSONAL_LOAN_VERTICAL,
      'Module Name': PL_PREQUAL_OFFER_MODULE,
    });
    dispatch({ type: RESET_SELECT_VERTICAL_TO_MARKETPLACE });
  };

  return {
    loading,
    sortLoading,
    sortBy,
    setSortBy,
    sortByList,
    prequalResult,
    otherAmountOffers,
    sameAmountOffers,
    hasOffers,
    onSortChange,
    onClickSort,
    showAllOffers,
    setShowAllOffers,
    showAllSameAmountOffers,
    fromPrequalForm,
    onClickSearchNoPrequal,
    onClickShowAllOffers,
    onClickShowAllSameAmountOffers,
    formLoanAmount,
    showAllPrequalOffers,
  };
};
